/**
 * Schema- and feature-aware help content.
 *
 * `buildHelpSections({ features, labels, schemaKey })` returns the manual
 * sections appropriate for the active deployment:
 *   - Sections are filtered by their `requires` feature flag, so the manual
 *     only describes panels that actually exist in this build.
 *   - Terminology adapts to the active schema's labels (e.g. "Proposal" for
 *     MaxIV, "Paragraph" for A2A), so a single source reads correctly for
 *     every client.
 *
 * Each section: { id, title, panelKey, requires?, short[], full[] }.
 * `requires` is a feature key (or array — any match shows the section).
 * Omit `requires` to always show the section.
 *
 * `short` = web-version text (condensed); `full` = extended manual text.
 */

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Pluralise without doubling a trailing "s" (so "Topics" stays "Topics").
const plural = (w) => (!w ? w : /s$/i.test(w) ? w : `${w}s`);

export function buildHelpSections({ features = null, labels = {}, schemaKey = '', facets = null, networkMeta = null } = {}) {
  const has = (key) => features === null
    || (Array.isArray(key) ? key.some((k) => features.includes(k)) : features.includes(key));
  // hasFacet: true when facets is unknown (null = not yet loaded → show everything)
  // or when the named facet is explicitly listed for this schema.
  const hasFacet = (f) => facets === null || facets.includes(f);

  const L = {
    record: labels.record || 'Proposal',
    document: labels.document || 'Document',
    industry: labels.industry || 'Industry',
    topic: labels.topic || 'Topic',
    themes: labels.themes || 'Themes',
    time: labels.time || 'Time',
  };
  const hasThemes = !!labels.themes;
  const isInterval = schemaKey === 'access2access'; // numeric start/end → overlap time filter

  const recLc = L.record.toLowerCase();
  const recsLc = plural(L.record).toLowerCase();
  const docLc = L.document.toLowerCase();
  const docsLc = plural(L.document).toLowerCase();
  const indLc = L.industry.toLowerCase();
  const indsLc = plural(L.industry).toLowerCase();
  const topLc = L.topic.toLowerCase();
  const themesLc = L.themes.toLowerCase();
  const timeLc = L.time.toLowerCase();

  // Tabs present in this build (for the orientation section).
  const tabs = [];
  if (has('overview')) tabs.push('Overview (headline numbers and charts)');
  if (has('network')) tabs.push('Network (the interactive collaboration graph)');
  if (has('heatmap') || has('umap')) tabs.push('Landscape (Heatmap and UMAP)');
  if (has('data')) tabs.push('Data (the underlying records as a table)');
  const tabsSentence = tabs.length
    ? `At the top you can switch between: ${tabs.join('; ')}.`
    : 'Use the tabs at the top to switch between views.';

  const timeShort = isInterval
    ? `${L.time}: drag the range to choose a period. A project appears whenever its active span (start–end) overlaps your selection, so multi-year work shows up in every ${timeLc} it was running. The slider is capped at the current year, even if some projects are scheduled to run beyond it.`
    : `${L.time}: drag the range to choose the periods to display. The view updates automatically.`;
  const timeFull = isInterval
    ? `${L.time}: the slider filters by a project's active period. A ${recLc} is included whenever its project's start–end span overlaps the selected range — so a project running 2019–2023 appears for any ${timeLc} in that span, not only its start ${timeLc}. ${plural(L.record)} whose project has no end date fall back to the start ${timeLc}. Note: the slider's upper bound is capped at the current year. Some projects have contracted end dates beyond that, but those future years are excluded from the selectable range as they do not yet reflect real activity.`
    : `${L.time}: you can adjust the time frame you want to display. The view updates automatically. By default the most recent periods are shown.`;

  // ── Section catalogue (pre-filter) ─────────────────────────────────────────
  const sections = [];

  // Orientation — always shown.
  sections.push({
    id: 'overview',
    title: 'Getting Started',
    panelKey: 'overview',
    short: [
      'Sign in, then explore your data through the tabs and the Data Filters on the left. The filter panel minimises automatically when not in use.',
      tabsSentence,
      'Everything stays in sync — any filter you apply updates every tab at once. Use Reset (top-right of the Data Filters) to clear them.',
    ],
    full: [
      'Open the dashboard in your browser and sign in with your credentials.',
      'Refine what you see with the Data Filters on the left. The panel minimises automatically when not in use, and Reset (top-right) clears every filter at once.',
      tabsSentence,
      'All views share the same filter state: a selection made on one tab is reflected everywhere. This lets you move between the headline charts, the spatial Landscape, and the source records without losing your place.',
      ...(has('summary') ? ['The right sidebar shows a running Summary of the current selection. Drag the divider handle to expand or collapse it.'] : []),
    ],
  });

  // The Overview analytics tab — one bullet per panel.
  if (has('overview')) {
    const ovShort = [
      'The Overview tab is your landing summary: a mix of headline numbers and compact, filter-aware charts. Most elements are clickable and apply themselves as a filter.',
      `Top strip — key counts for the current slice: ${recsLc}, ${docsLc}, projects, ${indsLc}, ${topLc}${hasThemes ? `, ${themesLc}` : ''}, ${L.time} span, and active filters.`,
    ];
    if (hasThemes) {
      ovShort.push(`${L.themes} coverage (donut): share of ${recsLc} per theme. Totals can exceed 100% because a ${recLc} may carry several themes. The ×N badge counts how many ${topLc} cross-cut that theme.`);
    }
    ovShort.push(`${L.industry} mix (treemap): each tile is one ${indLc}; tile size reflects how many ${recsLc} belong to it. Click a tile to filter.`);
    ovShort.push(`Top ${L.topic}: the largest ${topLc} by volume, each tagged with its dominant ${indLc} (blue chip)${hasThemes ? ' and the themes it touches (orange chips)' : ''}.`);
    ovShort.push(`Top ${plural(L.document)}: the ${docsLc} contributing the most ${recsLc} to the current slice — click to open one.`);
    if (hasThemes) {
      ovShort.push(`Unclaimed ${L.topic} — opportunity map: volume (x) against theme coverage (y). Points drifting to the warm bottom-right are candidates for new ${themesLc}.`);
    }
    ovShort.push(`Concept cloud: the most frequent terms across ${topLc} labels in the current slice.`);

    sections.push({
      id: 'overviewTab',
      title: 'Overview',
      panelKey: 'overview',
      requires: 'overview',
      short: ovShort,
      full: [
        'The Overview tab gives a fast read on the current selection. Every panel reacts to the Data Filters, and most are interactive — clicking an element (a tile, a row, a point) applies it as a filter across all tabs.',
        ...ovShort.slice(1),
        'Use it as a starting point: skim the headline counts, spot the dominant themes and clusters, then jump into the Landscape or open a source document to dig deeper.',
      ],
    });
  }

  // Data filters — always shown.
  const dfShort = [timeShort,
    `${L.industry}: filter to specific ${indsLc} from the dropdown — pick from the list or type to search.`,
    `${L.topic}: filter to specific ${topLc} the same way.`,
  ];
  if (hasThemes) dfShort.push(`${L.themes}: filter to specific ${themesLc}; a ${recLc} can match several.`);
  if (hasFacet('status')) {
    dfShort.push('Status: filter by proposal status — Accepted, Finished, Merged, Not Performed, Refused, Reserve List.');
  }
  if (hasFacet('orgType')) {
    dfShort.push('Org Type: filter by organisation type — Company, University, Other, Unspecified.');
  }
  if (hasFacet('organisation')) {
    dfShort.push('Organisation: filter to specific organisations in the network.');
  }
  const dfFull = [timeFull,
    `${L.industry}: click the ${L.industry} dropdown to choose specific ${indsLc}. Select from the menu or start typing to search, then tick the boxes you want.`,
    `${L.topic}: the ${L.topic} dropdown works the same way. With many ${topLc}, labels are shortened to their two most distinctive keywords for readability.`,
  ];
  if (hasThemes) dfFull.push(`${L.themes}: filter to one or more ${themesLc}. Themes are a transverse layer — a single ${recLc} can be tagged with several at once, so theme filters are inclusive (any match).`);
  if (hasFacet('status')) {
    dfFull.push('Status: filter for specific proposal statuses. Available values: Accepted, Finished, Merged, Not Performed, Refused, Reserve List.');
  }
  if (hasFacet('orgType')) {
    dfFull.push('Org Type: filter by the type of organisation — Company, University, Other, Unspecified.');
  }
  if (hasFacet('organisation')) {
    dfFull.push('Organisation: filter to specific organisations. Select from the menu or type to search.');
  }
  sections.push({
    id: 'dataFilter',
    title: 'Data Filters',
    panelKey: 'filterPanel',
    short: dfShort,
    full: dfFull,
  });

  // ── Network-only sections — hidden when no network feature ─────────────────
  // networkMeta is non-null only for multilayer schemas (e.g. MARVIN); it carries
  // the view list from schema.py → /api/config. MaxIV has no views block so
  // networkMeta is null there, keeping the existing MaxIV prose.
  const hasViews = !!(networkMeta?.views?.length);
  const viewNames = hasViews
    ? networkMeta.views.map((v) => v.name).join(', ')
    : '';
  const networkShort = hasViews ? [
    `The network has Nodes and Edges. Each Node represents a ${recLc} or one of its associated entities (${indsLc}, affiliations, authors, or categories depending on the active view). Node size scales with the number of connections.`,
    `Use the view tabs in the toolbar to switch between layouts: ${viewNames}.`,
  ] : [
    'The network has Nodes and Edges. Each Node represents a specific entity: Organisation (Company, University, Other), Affiliation, or Beamline. Node sizes scale with the number of proposals they are connected to.',
    'Each Edge represents the proposals that connect the different Nodes. Nodes have distinct shapes and colours for each entity type.',
    'Organisation: Collaboration partners listed in a proposal. Beamline: The specific beamline requested. Affiliation: The home institution of the principal investigator.',
  ];
  const networkFull = hasViews ? [
    `The network shows relationships between ${recsLc} and the entities connected to them. Each Node is either a ${recLc} or an entity in one of the configured layers — which layers appear depends on the active view.`,
    `Each Edge represents a relationship derived from the data (e.g. a ${recLc} belonging to a ${indLc}, or two ${indsLc} connected through shared ${recsLc}). Node size reflects the number of connections.`,
    `Hover any node to see its details; click to select it and open the Node Details panel.`,
    `Views — use the view tabs in the toolbar to switch between layouts:`,
    ...networkMeta.views.map((v) => `${v.name}: ${v.desc}`),
    ...(networkMeta.peopleLayers?.length > 1
      ? [`The ${networkMeta.peopleLabel || 'People'} toggle switches between ${networkMeta.peopleLayers.map((p) => (typeof p === 'string' ? p : p.label)).join(' and ')} as the applicant-level layer for views that include it.`]
      : []),
  ] : [
    'The network has Nodes and Edges. Each Node represents a specific entity in the network. This can be Organisations (Company, University, Other), Affiliation or Beamline. Node sizes scale with the number of proposals they are connected.',
    'Each Edge represents the proposals that connect the different Nodes. The nodes have different shapes and colours representing different kinds of entities in the network.',
    'Organisation: Represents the collaboration partners listed in a proposal, categorized as Companies, Universities, or Other (e.g., funding organizations and research councils).',
    'Beamline: The specific beamline requested within the proposal.',
    'Affiliation: The primary home institution indicated by the applicant (principal investigator / proposal owner) in the proposal.',
    'How to read the network: Consider a small network with one proposal — a collaboration between University of Oslo, SINTEF and FinEstBeAMS. A PI from University of Oslo (Affiliation) applies for beamtime at FinEstBeAMS (Beamline) and collaborates with SINTEF (Organisation). SINTEF, an independent research organisation, is categorised as Other.',
  ];
  sections.push({
    id: 'network',
    title: 'Network',
    panelKey: 'network',
    requires: 'network',
    short: networkShort,
    full: networkFull,
  });
  sections.push({
    id: 'nodeDetails',
    title: 'Node Details',
    panelKey: 'network',
    requires: 'network',
    short: [
      `Click any node to open the Node Details panel at the bottom. This view displays the Node Type, a list of all Connections to other nodes, and details of associated ${plural(L.record)} (ID, title, assigned ${L.industry} & ${L.topic}).`,
    ],
    full: [
      `Click any node to open the Node Details panel at the bottom. This view displays the Node Type, a list of all Connections to other nodes, and the details of the associated ${plural(L.record)} (ID, title, assigned ${L.industry} & ${L.topic}).`,
    ],
  });
  // Strategic Fine-Tuning is only relevant when the 'collaboration' facet is
  // present in this schema's facet list (currently MaxIV only).
  if (hasFacet('collaboration')) sections.push({
    id: 'strategicFineTuning',
    title: 'Strategic Fine-Tuning',
    panelKey: 'filterPanel',
    requires: 'network',
    short: [
      'This panel provides advanced filters to increase the granularity of your data analysis, managing the relationship between unspecified entities and their collaboration type.',
      'Unspecified Toggle (ON / OFF / ONLY): Segments proposals into those that name a concrete organisation (specified) and those that do not (unspecified).',
      'Collaboration Type: Filter by partnership nature — Actual, Potential, or No Connection.',
    ],
    full: [
      'This panel provides advanced filters to increase the granularity of your data analysis. It manages the relationship between unspecified entities and their collaboration type.',
      'Unspecified Toggle: A tripartite switch with three states — ON, OFF, and ONLY. It segments all proposal data into two categories: proposals that identify a concrete organisation within the text (specified) and those that do not (unspecified). It also functions as a master filter for the Collaboration Type selection.',
      'Collaboration Type: A selection menu to filter by the nature of the partnership: Actual, Potential or No Connection.',
      'Examples — Actual specified: "We collaborate with Company X." Actual unspecified: "We are collaborating with a major Swedish company." Potential specified: "Our research results could be of interest to Company X." Potential unspecified: "Our research results could be of interest to Swedish companies." No Connection: The industrial connection field is left blank.',
      'ON (Default): Includes all proposals regardless of whether a specific organization is identified. Includes actual, potential, and "no connection."',
      'OFF: Only displays proposals that name a concrete organisation. Note: "No Connection" is disabled because it is by definition unspecified.',
      'ONLY: Displays only proposals that lack an identified organization. Includes actual, potential, and "no connection."',
    ],
  });

  // Summary sidebar.
  sections.push({
    id: 'summary',
    title: 'Summary',
    panelKey: 'summary',
    requires: 'summary',
    short: [
      `The right sidebar shows totals for the current view and expandable breakdowns by ${indLc} and ${topLc}. All filters affect these figures.`,
    ],
    full: [
      `The Summary in the right sidebar shows the totals for your current selection, with expandable breakdowns (such as ${plural(L.industry)} and ${L.topic}). Use the expand/collapse icons to show or hide each breakdown.`,
      'Every Data Filter affects this display, so it always reflects exactly what is on screen.',
    ],
  });

  // Network toolbar tools.
  sections.push({
    id: 'screenshot',
    title: 'Screenshot',
    panelKey: 'network',
    requires: 'network',
    short: ['Press the camera icon (📷) in the upper right corner to export a high-resolution screenshot of the current network as a .png file.'],
    full: ['Pressing the Screenshot button exports a screenshot of the current network view as a .png file. Whatever is shown in the window at the moment is captured.'],
  });
  sections.push({
    id: 'companyLabels',
    title: 'Hide / Show Node Labels',
    panelKey: 'network',
    requires: 'network',
    short: ['Press the label icon (🏷️) to toggle node label visibility. Useful when preparing screenshots for public use.'],
    full: ['Press the Hide/Show Node Labels button to toggle the visibility of labels in the network. The labels will disappear. Press again to make them re-appear. Useful when preparing screenshots or recordings for public use.'],
  });
  sections.push({
    id: 'industryAura',
    title: `${L.industry} Aura`,
    panelKey: 'network',
    requires: 'network',
    short: [
      `Press the aura icon (🔬) to toggle the ${L.industry} Aura overlay. Each node is surrounded by a colored glow representing its ${indLc} distribution.`,
      'The legend (bottom-left) shows the color mapping. Use Intensity to control opacity, and Spread to expand the glow from tight petals to a broader heatmap view.',
    ],
    full: [
      `Press the ${L.industry} Aura button (🔬) in the toolbar to activate the overlay. Each node in the network is surrounded by a soft, colored glow — the "aura" — representing the ${indLc} distribution of the ${recsLc} connected to that node.`,
      `When a node is linked to ${recsLc} spanning multiple ${indsLc}, the aura splits into color petals, each sized by the proportion of ${recsLc} belonging to that ${indLc}. This makes it easy to spot which parts of the network are dominated by a single ${indLc} and where ${indsLc} overlap.`,
      'Intensity: Controls the opacity of the aura. At low values the overlay is subtle and the underlying node shapes remain clearly visible. At high values the aura becomes solid and nodes fade into the background, creating a heatmap-like effect.',
      `Spread: Controls how far the glow extends from the node center. At the "Tight" end each ${indLc} petal is compact and distinct. Moving toward "Heatmap" expands the radius so neighboring auras blend together, revealing broader ${indLc} clusters across the network.`,
      `A legend appears in the bottom-left corner listing each active ${indLc} and its assigned color. The legend is only visible while the overlay is active.`,
    ],
  });
  sections.push({
    id: 'graphSettings',
    title: 'Graph Settings',
    panelKey: 'network',
    requires: 'network',
    short: [
      'The gear icon (⚙️) opens the Graph Settings panel with Physics Controls and Edge Filter.',
      'Physics Controls: Toggle the simulation on/off, adjust Gravity, Label Spacing, and Island Pull.',
      'Edge Filter (Connection Strength): Filter edges by weight to reduce noise and surface the most significant collaborations.',
    ],
    full: [
      'The Graph Settings panel allows you to fine-tune the dynamic behavior and layout of your network visualization. It has two main categories: Physics Controls and Edge Filter.',
      'Simulation: When ON, nodes move dynamically to find optimal positions. When OFF, nodes remain fixed — useful for moving nodes around or "freezing" a layout.',
      'Gravity: Controls the global force pulling all nodes toward the center. Ranges from Expand (less pull) to Contract (stronger pull).',
      'Label Spacing: Adjusts the repulsive force between text labels. Ranges from Off (default positions) to Max (pushes labels apart to prevent overlap).',
      'Island Pull: Controls the force on disconnected node groups. Ranges from Loose (components drift away) to Tight (pulled toward center).',
      'Connection Strength: Controls edge visibility based on weight/frequency. ALL (default = 1) shows every connection. Moving the slider right filters to show only connections meeting or exceeding the selected strength. Node and edge statistics are displayed for the current setting.',
    ],
  });
  sections.push({
    id: 'export',
    title: 'Export & Network Statistics',
    panelKey: 'export',
    requires: 'export',
    short: [
      'The Export function downloads a ZIP containing CSV files (affiliations, beamlines, heatmap data, etc.) and saved analytics plots.',
      'The ZIP includes Network Statistics: Average Degree, Density, Modularity, and Betweenness Centrality.',
    ],
    full: [
      'Located above the toolbar, the Export function downloads a ZIP folder containing all current network data as CSV files (affiliations, beamlines, heatmap raw data etc.) and saved plots of your analytics and topic distributions.',
      'Average Degree: The average number of connections each node has. High = highly interconnected; Low = sparser.',
      'Density: The ratio of actual connections to total possible connections (0 to 1). Closer to 1 = denser network.',
      'Modularity: How well the network is organized into distinct communities/clusters. High = clear separate groups; Low = blended, no clear division.',
      'Betweenness Centrality: Identifies "influencers" or "bottlenecks." Measures how often a node acts as a bridge along the shortest path between two others.',
    ],
  });

  // ── Landscape: Heatmap + UMAP ──────────────────────────────────────────────
  const hmShort = [
    `The Heatmap cross-references ${plural(L.industry)} (rows) against ${L.topic} (columns) for the current filter selection. Colour intensity shows ${recLc} density — hover any cell for the exact count.`,
    'Labels are shortened to two distinctive keywords so dense grids stay readable.',
  ];
  if (has('viewer')) hmShort.push(`Click a cell to open a representative ${docLc} for that ${indLc} × ${topLc} slice.`);
  sections.push({
    id: 'heatmap',
    title: 'Heatmap',
    panelKey: 'analytics',
    requires: 'heatmap',
    short: hmShort,
    full: [
      `The Heatmap cross-references ${plural(L.industry)} (rows) against ${L.topic} (columns) for all ${recsLc} currently active in your filter selection. The colour intensity of each cell represents the density of ${recsLc} at that intersection. Hover over any cell to reveal the precise count.`,
      `Row and column totals are shown alongside the grid so you can see which ${indsLc} and ${topLc} dominate the current slice.`,
      `${cap(topLc)} labels are condensed to their two most distinctive keywords (the raw topic id and repeated/plural keywords are dropped) so the axis stays legible even with many columns.`,
      ...(has('viewer') ? [`Click a cell to open a representative source ${docLc} for that ${indLc} × ${topLc} combination.`] : []),
    ],
  });

  const umapShort = [
    `Each dot is one ${recLc}, placed by the meaning of its text and coloured by ${topLc}. Dots that sit close together talk about similar things.`,
    'Distance matters: tight clusters share a profile; islands are distinct groups.',
    'The layout may rotate or shift between runs — that is normal. Only the neighbourhoods (who sits next to whom) carry meaning.',
  ];
  if (has('viewer')) umapShort.push(`Click a dot to open the exact ${docLc} passage it represents; shift-click to list ${docsLc} for that ${topLc}.`);
  sections.push({
    id: 'umap',
    title: 'UMAP Projection',
    panelKey: 'analytics',
    requires: 'umap',
    short: umapShort,
    full: [
      `UMAP (Uniform Manifold Approximation and Projection) is a dimensionality-reduction technique. Each dot represents a single ${recLc}, coloured by ${topLc} (see the legend). Hover a dot to see its details and a title excerpt.`,
      'The model analyses the text to generate "embeddings" — mathematical representations in a high-dimensional space (often hundreds of dimensions). UMAP acts as a 2-D "shadow" of that space, preserving the original relationships and relative distances.',
      'How to read it: distance matters. Close dots = similar characteristics. Clouds = groups sharing a common profile. Islands = distinct groups with no shared vocabulary. Outliers = unusual items that differ markedly from the rest.',
      'Why does the layout change? UMAP does not care about fixed coordinates (left/right, top/bottom) — only about neighbourhoods. The algorithm uses randomness to find the best flattening, so orientation can vary between runs.',
      'Analogy: think of people standing in a room. Photos from different angles show them in different positions, but who stands next to whom never changes. In UMAP, neighbours stay the same even if the "room" rotates.',
      ...(has('viewer') ? [`Click a dot to open the exact ${docLc} passage it represents, with surrounding context. Shift-click to list the ${docsLc} for that dot's ${topLc}.`] : []),
    ],
  });

  // Document viewer.
  sections.push({
    id: 'viewer',
    title: 'Document Viewer',
    panelKey: 'viewer',
    requires: 'viewer',
    short: [
      `From a ${topLc}, cluster, heatmap cell, or scatter point you can drill into the source ${docsLc} and read them inline — no export needed.`,
      `The viewer highlights the relevant passage with a few paragraphs of context above and below. The header shows the project, ${timeLc}, ${indLc}, and page count.`,
      'Use "Get source" to open the original record (the CORDIS project page) in a new tab.',
    ],
    full: [
      `The document viewer lets you move from a pattern to the evidence behind it. Clicking a ${topLc}, heatmap cell, or UMAP dot opens a list of representative ${docsLc}, or jumps straight to the specific passage.`,
      `Inside the viewer, the relevant paragraph is highlighted with ±2–3 paragraphs of context so you can read it in situ. The header strip shows the project acronym, title, framework programme, ${timeLc}, ${indLc}, and page count.`,
      '"Get source" opens the authoritative CORDIS project page in a new tab — always current, nothing to host.',
    ],
  });

  // Data table.
  sections.push({
    id: 'dataViewer',
    title: 'Data Viewer',
    panelKey: 'data',
    requires: 'data',
    short: [
      `The Data tab displays the underlying ${recLc}-level records matching your current filters and selection in a spreadsheet-style table.`,
      'Search: Type in the search bar to instantly filter across all columns. Sorting: Click any column header to sort ascending or descending.',
      'Columns: Use the "Columns" dropdown to show or hide individual columns. Pagination: Navigate pages or change the page size (25, 50, 100, 250).',
    ],
    full: [
      `The Data tab provides a read-only, spreadsheet-style view of the ${recLc}-level records that match your current dashboard state — including all Data Filters and any selection.`,
      'This lets you inspect exactly which records are driving the patterns you see in the other tabs, without needing to export first.',
      'Search: The search bar performs a full-text search across every visible column. Results update as you type.',
      'Sorting: Click any column header to sort by that column; click again to reverse. An arrow indicator shows the active sort.',
      'Column Visibility: Click "Columns ▾" to open a checklist of all available columns and hide the ones you do not need.',
      'Pagination: Use the controls at the bottom to navigate and to change the number of rows per page (25, 50, 100, or 250). The total row count is shown in the toolbar.',
    ],
  });

  // Insight layer.
  const hasRag = has('rag');
  const insightShort = [
    'The insight layer reads the data on your current tab and writes a small set of grounded observations about it — adapting to your active tab, filters, and selection. The card header shows the lens it is using (e.g. "evidence_analyst | insight").',
    'Cards appear in the lower-right corner and can be dragged, minimised, or dismissed. Each card carries a category tag (pattern, trend, anomaly, comparison, concentration), a short title and explanation, and a "From the data" strip listing the exact figures behind the claim so you can check it against the view.',
    'Under "Explore further" each card suggests follow-up questions: 📊 ones are answered from the dashboard\'s own numbers, 📄 ones search the document text. Click a suggestion — or "Ask about this" — to open the chat with that question already framed.',
    hasRag
      ? 'In the chat, every answer is labelled by what it drew on — 📊 Analytics (dashboard figures), 📄 Documents (the source corpus), or both — and the question is routed there automatically. Document-backed answers list their sources (project · programme · paragraph), each opening in the Document Viewer, and report how many of the answer\'s claims were verified against those sources.'
      : 'The chat lets you ask follow-up questions about the current view. Answers are grounded in the same filtered data and reference entities by their exact names.',
    hasRag
      ? 'You can see — and correct — how the chat chose. The "why" link next to the engine badge shows the routing reason and the exact query that was searched; "Re-run as" lets you force Analytics, Documents, or both. Click any verified claim to highlight the source passage behind it.'
      : 'You can see — and correct — how the chat chose: the "why" link next to the engine badge shows the routing reason, and "Re-run as" lets you re-answer on a different engine.',
    'Drag the chat panel\'s left edge to make it wider or narrower; the size is remembered for next time.',
    'Toggle the whole layer on or off with the "insight" button in the tab bar. Insights are AI-generated — always verify before relying on them.',
  ];
  const insightFull = [
    'The insight layer analyses the data visible on your current tab and generates a small set of observations — typically 3 to 5 cards — highlighting patterns, concentrations, anomalies, or notable details. The voice and framing are set by the deployment\'s persona, shown in the card header alongside the word "insight".',
    'Insights are scoped to your exact view: they reflect the active filters, time selection, and any node or cell selection. With no selection they cover the full filtered dataset.',
    'Each card shows a category tag, a short title, and a brief explanation. Below the text, a "From the data" strip lists 1–3 figures or labels copied straight from the current view — the actual numbers the insight is based on — so you can verify the claim without trusting the prose alone.',
    'Under "Explore further", a card offers follow-up questions tailored to its finding. A 📊 question is answerable from the dashboard\'s structure and metrics (sizes, counts, distances); a 📄 question requires reading the underlying document text. Clicking one opens the chat with the question pre-filled; "Ask about this" opens the chat focused on the card\'s topic.',
    'Navigate between cards with the left/right arrows. The minimise button collapses the deck to a small pill; the dismiss (×) button hides it until the view changes. The whole card can be dragged out of the way by its header bar.',
    hasRag
      ? 'The chat (via the chat icon) routes each question automatically to the right source: 📊 Analytics for structural or quantitative questions about the current view, 📄 Documents for questions about what the source text actually says, or both when a question needs each. The answer is tagged with which engine was used.'
      : 'The chat (via the chat icon) lets you ask questions about the data in your current view, using the same filtered dataset as context. Conversation history is kept within the session.',
    ...(hasRag ? [
      'Document-backed answers cite their evidence. Expand "N sources" to see each cited passage — labelled by project acronym and framework programme, with its paragraph anchor (¶) — and click a source to open it in the Document Viewer, or follow the ↗ link to the project\'s CORDIS page.',
      'Answers also carry a verification badge ("X/Y claims verified"). Expand it to see each individual claim checked against the retrieved sources and marked ✓ verified, ~ unverified, or ✗ not in source — so you can see exactly which parts of the answer the documents support. Click a claim to highlight the specific source(s) it rests on (the others dim); click "show all" to clear the focus.',
    ] : []),
    'Each answer is transparent about how it was produced. The "why" link beside the engine badge reveals the routing reason (why the question went to Analytics, Documents, or both)' + (hasRag ? ', and the rewritten query that was actually searched against the corpus' : '') + '. If the routing looks wrong, use "Re-run as" to force a different engine and compare — your original question is re-sent unchanged.',
    'You can resize the chat by dragging its left edge; the chosen width is saved for your next visit. Use Export to download the whole conversation — engine, sources, and per-claim verification included — as a Markdown file for sharing or record-keeping.',
    'The insight toggle appears in the tab bar as "insight". When active, switching tabs or changing filters triggers new insight generation; when inactive, no insight or chat requests are sent.',
    'If the current selection contains too few data points for meaningful analysis, a short message explains why no insights were generated, with a retry option. Insights and chat answers are AI-generated and may contain errors — always verify against the underlying data and sources.',
  ];
  sections.push({
    id: 'insight',
    title: 'Insight',
    panelKey: 'insight',
    requires: 'insight',
    short: insightShort,
    full: insightFull,
  });

  // ── Filter by feature presence ─────────────────────────────────────────────
  return sections.filter((s) => !s.requires || has(s.requires));
}

export default buildHelpSections;
